import React from 'react';
import { 
  Typography,
  Grid,
  Box,
  styled
} from '@mui/material';
import { formatLicensePlate } from '../helpers/formatLicensePlate';
import PropTypes from 'prop-types';
import BasicPlanCard from './BasicPlanCard';
import PremiumPlanCard from './PremiumPlanCard';

// Componente estilizado para o invoice com as bordas decorativas
const InvoiceBox = styled(Box)(({ theme }) => ({
  backgroundColor: '#fff',
  width: '275px',
  position: 'relative',
  margin: theme.spacing(3.75, 2.5),
  paddingTop: theme.spacing(2),
  background: 'linear-gradient(180deg, #FFFFFF 0%, #EBF0F3 36.11%)',
  
  // Borda superior tipo recibo
  '&::before': {
    content: '""',
    position: 'absolute',
    top: -8, // Metade para fora
    left: -15, // Extende além das laterais
    right: -15,
    height: '16px', // Altura total da borda
    background: '#333333', // Cor escura para parecer a borda do recibo
    zIndex: 1, // Garante que fique acima do conteúdo
  },
  
  // Triângulos inferiores
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: -12,
    left: 0,
    right: 0,
    height: '12px',
    background: '#EBF0F3',
    clipPath: 'polygon(0% 0%, 3% 100%, 6% 0%, 9% 100%, 12% 0%, 15% 100%, 18% 0%, 21% 100%, 24% 0%, 27% 100%, 30% 0%, 33% 100%, 36% 0%, 39% 100%, 42% 0%, 45% 100%, 48% 0%, 51% 100%, 54% 0%, 57% 100%, 60% 0%, 63% 100%, 66% 0%, 69% 100%, 72% 0%, 75% 100%, 78% 0%, 81% 100%, 84% 0%, 87% 100%, 90% 0%, 93% 100%, 96% 0%, 99% 100%, 100% 0%)'
  }
}));

// Componente estilizado para a placa
const LicensePlateBox = styled(Box)(({ theme }) => ({
  backgroundColor: '#FFFFFF',
  borderRadius: theme.spacing(1.5),
  borderTop: '15px solid #45e2b1',
  textAlign: 'center',
  maxWidth: '160px',
  margin: '0 auto',
  padding: theme.spacing(1, 0),
}));

// 1. Constantes para reuso
const COLORS = {
  primary: '#012F47',
  secondary: '#239CC4',
  accent: '#45e2b1',
  white: '#FFFFFF',
  border: '#333333',
  gradient: 'linear-gradient(180deg, #FFFFFF 0%, #EBF0F3 36.11%)',
};

const formatPrice = (price) => {
  if (!price) return '0,00';
  return price.toFixed(2).replace('.', ',');
};

const PriceDisplay = React.memo(({ price }) => (
  <Box sx={{ 
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'center',
    gap: 1,
    mt: { xs: 5, sm: 5, md: 6 }
  }}>
    <Typography 
      variant="caption" 
      sx={{ 
        fontSize: { xs: '1.25rem', sm: '1.5rem', md: '1.75rem' },
        fontWeight: 500,
        mt: -0.5
      }}
    >
      R$
    </Typography>
    <Typography 
      variant="h2" 
      sx={{ 
        fontWeight: 400,
        fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' }
      }}
    >
      {formatPrice(price)}
    </Typography>
  </Box>
));

// 3. Estilos comuns
const commonBoxStyles = {
  bgcolor: COLORS.white,
  borderRadius: { xs: 2, sm: 3, md: 4 },
  boxShadow: '0px 6px 24px 0px #012F473A',
};

// 4. PropTypes para melhor documentação
OrderDetails.propTypes = {
  licensePlate: PropTypes.string,
  price: PropTypes.number,
  planType: PropTypes.string
};

// 5. Componente principal otimizado
export default function OrderDetails({ licensePlate = '', price = 0, planType = ''}) {
  return (
    <Box sx={{ 
      ...commonBoxStyles,
      maxWidth: { xs: '100%', lg: '1200px' },
      mx: 'auto',
      p: { xs: 5, sm: 5, md: 10 },
      pt: { xs: 5, sm: 5, md: 5 },
      mb: 5
    }}>
      <Box sx={{ 
        display: 'flex',
        flexDirection: 'column',
        gap: { xs: 3, md: 4 },
      }}>
        <Typography 
          variant="h1" 
          sx={{
            textAlign: 'center',
            fontWeight: 700,
            fontSize: { xs: '22px', md: '30px' },
            color: '#012F47',
          }}
        >
          Detalhes da compra
        </Typography>

        <Grid container spacing={4}>
          {/* Card da Esquerda - Plano */}
          <Grid item xs={12} md={7}>
            {/* PREMIUM COMENTADO - original: planType === 'basic' ? <BasicPlanCard /> : <PremiumPlanCard /> */}
            <BasicPlanCard />
          </Grid>

          {/* Card da Direita - Resumo */}
          <Grid item xs={12} md={5}>
            <Box sx={{ 
              p: { xs: 2, sm: 3, md: 4 },
              height: '100%',
              display: 'flex',
              justifyContent: 'center',
              minHeight: { // Garante altura mínima do container
                xs: '350px',
                sm: '350px',
                md: '280px'
              }
            }}>
              <InvoiceBox>
                <Box sx={{ mt: 5 }}>
                  <LicensePlateBox>
                    <Typography
                      sx={{
                        fontSize: '24px',
                        fontWeight: 800,
                        color: '#012F47',
                      }}
                    >
                      {formatLicensePlate(licensePlate)}
                    </Typography>
                  </LicensePlateBox>
                </Box>
                <PriceDisplay price={price} />
              </InvoiceBox>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
}